// On-device search across the site's own content (no server, no AI): roles,
// projects, skills, education and training. Every word you type must match the
// start of a word somewhere in an entry (so "kube" finds Kubernetes).

const TYPE_ORDER = ['Experience', 'Project', 'Skill', 'Education', 'Training'];

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const startsAWord = (term) => new RegExp(`(^|[^a-z0-9])${escapeRegExp(term)}`, 'i');
const wholeWord = (term) => new RegExp(`(^|[^a-z0-9])${escapeRegExp(term)}([^a-z0-9]|$)`, 'i');

export function buildIndex({ experience = [], projects = [], skills = [], education = [], certifications = [] }) {
    const entries = [];

    experience.forEach((job) => {
        entries.push({
            id: `exp-${job.id}`,
            type: 'Experience',
            section: 'experience',
            title: `${job.jobtitle}, ${job.company}`,
            tags: [job.company],
            snippets: [job.summary, ...(job.highlights || [])].filter(Boolean),
        });
    });

    projects.forEach((project) => {
        entries.push({
            id: `proj-${project.id}`,
            type: 'Project',
            section: 'projects',
            title: project.projectName,
            tags: project.tags || [],
            snippets: [project.projectDesc].filter(Boolean),
        });
    });

    skills.forEach((group) => {
        group.items.forEach((item) => {
            entries.push({
                id: `skill-${item}`,
                type: 'Skill',
                section: 'skills',
                title: item,
                tags: [group.category],
                snippets: [],
            });
        });
    });

    education.forEach((item) => {
        entries.push({
            id: `edu-${item.id}`,
            type: 'Education',
            section: 'education',
            title: `${item.course}, ${item.institution}`,
            tags: [],
            snippets: [item.detail].filter(Boolean),
        });
    });

    certifications.forEach((cert) => {
        entries.push({
            id: `cert-${cert}`,
            type: 'Training',
            section: 'education',
            title: cert,
            tags: [],
            snippets: [],
        });
    });

    return entries;
}

const shorten = (text, max = 120) => (text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text);

export function search(query, index, limit = 8) {
    const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return [];

    const results = [];
    index.forEach((entry) => {
        let score = 0;
        const ok = tokens.every((token) => {
            const starts = startsAWord(token);
            const whole = wholeWord(token);
            const inTitle = starts.test(entry.title);
            const inTags = entry.tags.some((tag) => starts.test(tag));
            const inText = entry.snippets.some((snippet) => starts.test(snippet));
            if (!inTitle && !inTags && !inText) return false;
            if (inTitle) score += whole.test(entry.title) ? 6 : 4;
            if (inTags) score += 3;
            if (inText) score += 1;
            return true;
        });
        if (!ok) return;

        const first = tokens[0];
        const matchedSnippet = entry.snippets.find((snippet) => startsAWord(first).test(snippet));
        const detail = matchedSnippet ? shorten(matchedSnippet) : (entry.tags[0] || '');
        results.push({ ...entry, score, detail });
    });

    return results
        .sort((a, b) => b.score - a.score || TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type))
        .slice(0, limit);
}
